import React, { useRef, useEffect, useState } from 'react';
import { useLumoChat } from '../../contexts/LumoChatContext';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';
import { useAuth } from '../../contexts/AuthContext';
import LumoStatsBar from './LumoStatsBar';

/**
 * LumoChat - Floating AI mentor chat interface
 * Always accessible via bottom-right button
 * Provides context-aware coaching, feature access, and Q&A
 */
const LumoChat = () => {
  const { user } = useAuth();
  const location = useLocation();
  const {
    isOpen,
    messages,
    isTyping,
    unreadCount,
    toggleChat,
    closeChat,
    sendMessage,
    executeAction
  } = useLumoChat();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      inputRef.current?.focus();
    }
  }, [messages, isOpen, isTyping]);
  
  // Don't render if user not authenticated
  if (!user) return null;

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue, location?.pathname);
      setInputValue('');
    }
  };

  const handleActionClick = (action) => {
    executeAction(action);
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary shadow-elevated hover:shadow-elevated-hover transition-all duration-300 flex items-center justify-center group"
          aria-label="Open Lumo Chat"
        >
          {/* Pulse animation for unread messages */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 bg-destructive text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse-subtle">
              {unreadCount}
            </span>
          )}
          
          {/* Icon with subtle animation */}
          <Icon 
            name="MessageCircle" 
            size={28} 
            color="#FFFFFF" 
            className="group-hover:scale-110 transition-transform duration-200"
          />
          
          {/* Glow effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 animate-pulse-slow" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-6rem)] bg-card border border-border rounded-2xl shadow-elevated flex flex-col overflow-hidden animate-slide-in-up">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gradient-to-r from-primary/10 to-accent/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Icon name="Sparkles" size={20} color="#FFFFFF" />
              </div>
              <div>
                <h3 className="font-bold text-foreground">Lumo</h3>
                <p className="text-xs text-muted-foreground">AI Sales Coach</p>
              </div>
            </div>
            <button
              onClick={closeChat}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              aria-label="Close chat"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>

          {/* Stats Bar - Mini Dashboard */}
          <LumoStatsBar />

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background/50">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "flex",
                  msg.type === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-3",
                    msg.type === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-accent/10 text-foreground border border-border'
                  )}
                >
                  {/* Message text */}
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.message}
                  </div>

                  {/* Action buttons */}
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {msg.actions.map((action, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleActionClick(action)}
                          className="w-full px-3 py-2 text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          {action.label}
                          {action.type === 'navigate' && (
                            <Icon name="ArrowRight" size={12} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Timestamp */}
                  <div className={cn(
                    "text-xs mt-2",
                    msg.type === 'user' ? 'text-white/70' : 'text-muted-foreground'
                  )}>
                    {formatTime(msg.timestamp)}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-accent/10 border border-border rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-border bg-card">
            <div className="flex items-end gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask Lumo anything..."
                className="flex-1 px-4 py-3 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                disabled={isTyping}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isTyping}
                className={cn(
                  "p-3 rounded-xl transition-colors",
                  inputValue.trim() && !isTyping
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              >
                <Icon name="Send" size={20} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              💡 Try: "Practice price objections" or "What's SPIN selling?"
            </p>
          </form>
        </div>
      )}
    </>
  );
};

export default LumoChat;
